/**
 * Gravação de um arquivo de dataset no `BF_SELLER_DATASETS`.
 *
 * O ciclo por arquivo é: marca o arquivo como `pending` no `__meta`, garante que a store foi
 * limpa, grava **um registro** com a chave tirada do caminho da URL e fecha o `__meta` como `ok`.
 * Não há leitura prévia nem merge em JavaScript — arquivo baixado é arquivo gravado.
 *
 * **A limpeza é por store, uma vez por sync — não por arquivo.** O backend manda vários arquivos
 * para a mesma store; se cada arquivo limpasse a store, sobraria só o último. A sessão guarda a
 * promessa da limpeza: o primeiro arquivo limpa, os outros esperam essa mesma limpeza terminar e
 * gravam.
 *
 * **E ela só limpa a store inteira quando não há arquivo íntegro para preservar.** O
 * `_useGetLinks` baixa arquivo por arquivo — arquivo republicado não arrasta os outros do mesmo
 * tipo —, então limpar sempre apagaria justamente os arquivos que não precisavam voltar. Com
 * arquivo íntegro no `__meta`, a limpeza tira só o registro `@local`, que é a tabela inteira e não
 * pode conviver com registro de arquivo. Sem nenhum, limpa tudo e leva o `__meta` da store junto —
 * é o que garante que uma queda no meio do sync não deixe meta dizendo "ok" para dado apagado.
 */

import type { DataSetsProps } from '@/types';
import { invalidateBfSellerCache } from './bfSellerCache';
import { BF_SELLER_META_STORE, openBfSellerDb } from './bfSellerDb';
import {
  BfSellerStore,
  DatasetScope,
  KeyPart,
  countItems,
  fileKeyFromUrl,
  getDatasetSpec,
  isImageDatasetType,
  resolveDatasetScope,
} from './bfSellerSchema';

export type DatasetWriteStatus = 'ok' | 'sem-linhas' | 'sem-spec' | 'imagens' | 'erro';

export type DatasetWriteResult = {
  ok: boolean;
  status: DatasetWriteStatus;
  tipo: string;
  store: BfSellerStore | null;
  scope: DatasetScope;
  /** Itens dentro do registro gravado — o registro é sempre um. */
  rows: number;
  error?: string;
  /** Tempo de cada fase, em ms — para achar o gargalo sem chutar. */
  timing?: {
    /** Payload → valor gravável. */
    normalize: number;
    /** `clear()` da store. */
    purge: number;
    /** O `put`. */
    put: number;
    total: number;
  };
};

export type DatasetMetaEntry = {
  tipo: string;
  /** `null` para imagens, que não têm store no `BF_SELLER_DATASETS` — só registro. */
  store: BfSellerStore | null;
  /** Loja e segmento do descritor: não fazem parte da chave, servem para diagnóstico. */
  loja: number;
  segmento: number;
  url?: string;
  /** Chave do registro gravado, que é o caminho do arquivo. */
  key?: KeyPart[];
  dtUpdate?: string;
  rows: number;
  status: 'pending' | 'ok';
  startedAt: string;
  finishedAt?: string;
  /** Quanto a gravação levou, em ms. Fica no banco para dar para conferir depois do sync. */
  ms?: number;
};

/** Uma entrada de `__meta` por arquivo, com a mesma identidade do registro. */
const metaKey = (tipo: string, key: KeyPart[]) => [tipo, ...key];

/**
 * Estado de um sync: quais stores já foram limpas neste run. Crie uma por execução de
 * `fetchDatasets` e passe para todos os arquivos daquele run.
 */
export type DatasetWriteSession = {
  purges: Map<BfSellerStore, Promise<void>>;
};

export const createDatasetWriteSession = (): DatasetWriteSession => ({ purges: new Map() });

/**
 * Grava um arquivo baixado. Nunca lança: devolve o resultado para o fluxo de sync montar o
 * relatório e decidir o que é bloqueante.
 */
export const writeDatasetFile = async ({
  tipo,
  dataset,
  payload,
  session,
}: {
  tipo: string;
  dataset: Pick<DataSetsProps, 'URL_S3' | 'DT_UPDATE'> & {
    COD_LOJA?: number | string | null;
    COD_SEGMENTO?: number | string | null;
  };
  payload: unknown;
  /** Omitir só faz sentido para gravação avulsa: sem sessão, cada arquivo limpa a própria store. */
  session?: DatasetWriteSession;
}): Promise<DatasetWriteResult> => {
  const scope = resolveDatasetScope(dataset);
  const base = { tipo, scope, rows: 0 };

  if (isImageDatasetType(tipo)) {
    return { ...base, ok: true, status: 'imagens', store: null };
  }

  const spec = getDatasetSpec(tipo);
  if (!spec) {
    console.warn(`[BF_SELLER_DATASETS] dataset sem spec, nada gravado: ${tipo}`);
    return { ...base, ok: false, status: 'sem-spec', store: null };
  }

  const url = dataset?.URL_S3 ?? '';

  const t0 = Date.now();
  let value: unknown | null;
  try {
    value = spec.toValue(payload, { url });
  } catch (error: any) {
    return { ...base, ok: false, status: 'erro', store: spec.store, error: `normalização: ${error?.message ?? error}` };
  }
  const msNormalize = Date.now() - t0;

  // Payload vazio não grava nada: um arquivo que veio sem conteúdo não pode zerar o que existe.
  if (value == null) {
    return { ...base, ok: false, status: 'sem-linhas', store: spec.store };
  }

  const rows = countItems(value);

  try {
    const db = await openBfSellerDb();
    const key = fileKeyFromUrl(url);
    const meta: DatasetMetaEntry = {
      tipo,
      store: spec.store,
      loja: scope.loja,
      segmento: scope.segmento,
      url,
      key,
      dtUpdate: dataset?.DT_UPDATE,
      rows,
      status: 'pending',
      startedAt: new Date().toISOString(),
    };

    // `pending` antes de limpar: se o app morrer no meio, o arquivo fica marcado como incompleto.
    await db.put(BF_SELLER_META_STORE, meta, metaKey(tipo, key));

    // Uma limpeza por store por sync. Quem chega depois espera a mesma e não limpa de novo.
    const purges = (session ?? createDatasetWriteSession()).purges;
    const tPurge = Date.now();
    let purge = purges.get(spec.store);
    if (!purge) {
      purge = (async () => {
        const purgeTx = db.transaction([spec.store, BF_SELLER_META_STORE], 'readwrite');
        const conteudo = purgeTx.objectStore(spec.store);
        const metaStore = purgeTx.objectStore(BF_SELLER_META_STORE);
        const [chaves, entradas] = await Promise.all([metaStore.getAllKeys(), metaStore.getAll()]);

        // Arquivo íntegro é registro de arquivo (tem `url`) fechado como `ok` com linhas. O
        // arquivo deste run não conta: ele acabou de virar `pending` na linha acima.
        const temArquivoIntegro = (entradas as DatasetMetaEntry[]).some(
          (entrada) => entrada?.store === spec.store && entrada?.status === 'ok' && Number(entrada?.rows) > 0 && !!entrada?.url
        );

        if (temArquivoIntegro) {
          // Os outros arquivos da store ficam. Sai só a tabela inteira que o app gravou local.
          void conteudo.delete(LOCAL_RECORD_KEY as unknown as IDBValidKey).catch(() => {});
        } else {
          void conteudo.clear().catch(() => {});

          // O meta dos arquivos desta store sai junto com o conteúdo — ver o cabeçalho.
          for (let i = 0; i < chaves.length; i += 1) {
            if ((entradas[i] as DatasetMetaEntry)?.store === spec.store) void metaStore.delete(chaves[i]);
          }
        }

        await purgeTx.done;
      })();
      purges.set(spec.store, purge);
    }
    await purge;
    const msPurge = Date.now() - tPurge;

    const tPut = Date.now();
    await db.put(spec.store, value, key as unknown as IDBValidKey);
    const msPut = Date.now() - tPut;

    const total = Date.now() - t0;

    await db.put(
      BF_SELLER_META_STORE,
      { ...meta, status: 'ok', finishedAt: new Date().toISOString(), ms: total } satisfies DatasetMetaEntry,
      metaKey(tipo, key)
    );

    return {
      ...base,
      ok: true,
      status: 'ok',
      store: spec.store,
      rows,
      timing: { normalize: msNormalize, purge: msPurge, put: msPut, total },
    };
  } catch (error: any) {
    return { ...base, ok: false, status: 'erro', store: spec.store, error: error?.message ?? String(error) };
  }
};

/** Chave do registro que o próprio app grava — não veio de arquivo nenhum. */
export const LOCAL_RECORD_KEY: KeyPart[] = ['@local'];

/**
 * Grava uma alteração que o app fez na tabela: a baixa de estoque e o saldo do cliente depois de
 * um pedido.
 *
 * O app sempre reescreve a **tabela inteira** (`tables.current.estoque.map(...)`), então o
 * registro local toma o lugar dos arquivos — a leitura concatena registros, e manter os dois
 * devolveria a lista duplicada. O próximo sync limpa a store e regrava os arquivos, que é o
 * certo: dado do servidor volta a mandar assim que chega.
 */
export const writeLocalDatasetTable = async (store: BfSellerStore, data: unknown): Promise<void> => {
  if (!Array.isArray(data)) {
    throw new Error(`[BF_SELLER_DATASETS] ${store}: alteração local precisa ser a lista completa da tabela`);
  }

  const db = await openBfSellerDb();
  const tx = db.transaction([store, BF_SELLER_META_STORE], 'readwrite');
  const conteudo = tx.objectStore(store);
  await conteudo.clear();
  await conteudo.put(data, LOCAL_RECORD_KEY as unknown as IDBValidKey);

  /**
   * O `__meta` acompanha: os arquivos daquela store deixaram de existir, e no lugar deles fica um
   * registro do `@local`. Sem isso o sync seguinte acharia que os arquivos ainda estão gravados e
   * baixaria só o que mudou — a tabela ficaria com esse arquivo e nada do resto. Com o registro do
   * `@local`, `hasDatasetRows` continua respondendo que a tabela tem dado (é verdade) e nenhum
   * arquivo conta como baixado, então o tipo volta inteiro no próximo sync.
   */
  const metaStore = tx.objectStore(BF_SELLER_META_STORE);
  const [chaves, entradas] = await Promise.all([metaStore.getAllKeys(), metaStore.getAll()]);
  for (let i = 0; i < chaves.length; i += 1) {
    if ((entradas[i] as DatasetMetaEntry)?.store === store) void metaStore.delete(chaves[i]);
  }

  const agora = new Date().toISOString();
  void metaStore.put(
    {
      tipo: store,
      store,
      ...resolveDatasetScope({}),
      key: LOCAL_RECORD_KEY,
      rows: data.length,
      status: 'ok',
      startedAt: agora,
      finishedAt: agora,
    } satisfies DatasetMetaEntry,
    metaKey(store, LOCAL_RECORD_KEY)
  );

  await tx.done;

  // O índice em memória ficou velho no mesmo instante.
  invalidateBfSellerCache();
};

/**
 * Registra no `__meta` um arquivo de imagens gravado.
 *
 * Imagens não entram em store do `BF_SELLER_DATASETS` (vão para o `BF_SELLER_IMAGES`), mas precisam do
 * mesmo registro dos outros arquivos: é por ele que o `_useGetLinks` sabe que aquele zip já veio.
 */
export const writeImageDatasetFile = async ({
  tipo,
  dataset,
  rows,
}: {
  tipo: string;
  dataset: Pick<DataSetsProps, 'URL_S3' | 'DT_UPDATE'> & {
    COD_LOJA?: number | string | null;
    COD_SEGMENTO?: number | string | null;
  };
  rows: number;
}): Promise<void> => {
  try {
    const db = await openBfSellerDb();
    const scope = resolveDatasetScope(dataset);
    const url = dataset?.URL_S3 ?? '';
    const key = fileKeyFromUrl(url);
    const agora = new Date().toISOString();

    await db.put(
      BF_SELLER_META_STORE,
      {
        tipo,
        store: null,
        loja: scope.loja,
        segmento: scope.segmento,
        url,
        key,
        dtUpdate: dataset?.DT_UPDATE,
        rows,
        status: 'ok',
        startedAt: agora,
        finishedAt: agora,
      } satisfies DatasetMetaEntry,
      metaKey(tipo, key)
    );
  } catch (error) {
    console.info('[BF_SELLER_DATASETS] falha ao registrar arquivo de imagens no __meta:', error);
  }
};

/**
 * Apaga o registro dos arquivos de imagem. Chamar quando a store de imagens é zerada, pelo mesmo
 * motivo do purge dos datasets: registro sem conteúdo faria o sync seguinte pular o download.
 */
export const clearImageDatasetMeta = async (): Promise<void> => {
  try {
    const db = await openBfSellerDb();
    const tx = db.transaction(BF_SELLER_META_STORE, 'readwrite');
    const store = tx.objectStore(BF_SELLER_META_STORE);
    const [chaves, entradas] = await Promise.all([store.getAllKeys(), store.getAll()]);
    for (let i = 0; i < chaves.length; i += 1) {
      if (isImageDatasetType((entradas[i] as DatasetMetaEntry)?.tipo)) void store.delete(chaves[i]);
    }
    await tx.done;
  } catch (error) {
    console.info('[BF_SELLER_DATASETS] falha ao limpar o registro de imagens:', error);
  }
};

/** Entradas do `__meta` de um tipo de dataset (todos os arquivos já gravados). */
export const readDatasetMeta = async (tipo?: string): Promise<DatasetMetaEntry[]> => {
  try {
    const db = await openBfSellerDb();
    const range = tipo ? IDBKeyRange.bound([tipo], [tipo, []]) : undefined;
    return (await db.getAll(BF_SELLER_META_STORE, range)) as DatasetMetaEntry[];
  } catch (error) {
    console.info('[BF_SELLER_DATASETS] falha ao ler __meta:', error);
    return [];
  }
};

/**
 * URLs dos arquivos gravados com sucesso.
 *
 * É a pergunta "este arquivo já foi baixado?", que antes vinha de uma lista paralela no
 * descartado pelo navegador, a resposta cai junto — não sobra registro apontando para dado que
 * não existe mais.
 */
export const readDownloadedUrls = async (): Promise<Set<string>> => {
  const entries = await readDatasetMeta();
  const urls = new Set<string>();
  for (const entry of entries) {
    if (entry?.status !== 'ok' || !(Number(entry?.rows) > 0) || !entry.url) continue;
    urls.add(entry.url);
  }
  return urls;
};

/**
 * Se já existe dado gravado. Substitui o `validateTable` do banco antigo na decisão de baixar ou
 * não um arquivo.
 *
 * Aceita tanto o `TIPO_ARQUIVO` (`preco_novo`) quanto o nome da tabela (`preco`), porque quem
 * pergunta são dois lugares diferentes: a lista de datasets da API fala em tipo, e
 * `OBRIGATORY_DB_TABLES` fala em tabela.
 */
export const hasDatasetRows = async (tipoOuTabela: string): Promise<boolean> => {
  const entries = await readDatasetMeta();
  return entries.some(
    (entry) => entry?.status === 'ok' && Number(entry?.rows) > 0 && (entry.tipo === tipoOuTabela || entry.store === tipoOuTabela)
  );
};
